import * as Sentry from '@sentry/react';
import { SwitchThemeContext, useSwitchTheme } from '@tryhackme/thm-ui-components';
import { useEffect, useMemo } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import 'react-datepicker/dist/react-datepicker.css';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import 'src/common/chart/chart-config';
import { store } from './app/store';
import { AppErrorDisplay } from './common/app-error-display';
import { ForceLogoutListener } from './common/components/force-logout-listener/force-logout-listener';
import { ThemeObserverWrapper } from './common/components/theme-observer';
import { AnonymousSaleProvider } from './common/contexts/anonymous-sale-context';
import { LayoutOverrideProvider } from './common/contexts/layout-override-context';
import { SoundProvider } from './common/contexts/sound-context';
import { GrowthBookProvider, gb } from './common/growthbook';
import { commonEndpoints } from './common/slices';
import { RouterProvider } from './router';

// eslint-disable-next-line @typescript-eslint/no-floating-promises
store.dispatch(commonEndpoints.getUser.initiate());
export const App = () => {
  useEffect(() => {
    /* eslint-disable no-console */
    const handleError = (event: ErrorEvent) => {
      if (event?.message?.includes('SyntaxError') && event?.message?.trim().endsWith("'<'") && event?.filename?.startsWith(window.location.origin)) {
        const url = new URL(window.location.href);
        if (url.searchParams.get('badBundle') === 'true') {
          if (url.pathname.replace(/^\/r/, '') === '/500') {
            setTimeout(() => {
              const root = document.querySelector('#root');
              if (root && !root?.innerHTML?.length) {
                root.innerHTML = '<h1>Fatal error! Please try again or contact customer support.</h1>';
              }
            }, 1000);
            console.error('Detected bad JS bundle loop, giving up');
            return;
          }
          console.error('Detected bad JS bundle again, going to 500...');
          window.location.href = '/500';
          return;
        }
        console.error('Detected bad JS bundle, reloading page...');
        url.searchParams.set('badBundle', 'true');
        window.location.href = url.toString();
      }
    };
    /* eslint-enable no-console */
    if (gb.isOn('reload-on-bad-bundle')) {
      window.addEventListener('error', handleError, true);
    }
    return () => {
      window.removeEventListener('error', handleError);
    };
  }, []);
  const {
    currentTheme,
    setCurrentTheme
  } = useSwitchTheme();
  const switchThemeContextValue = useMemo(() => ({
    theme: currentTheme,
    setTheme: setCurrentTheme
  }), [currentTheme, setCurrentTheme]);
  return <HelmetProvider data-sentry-element="HelmetProvider" data-sentry-component="App" data-sentry-source-file="app.tsx">
      <Provider store={store} data-sentry-element="Provider" data-sentry-source-file="app.tsx">
        <GrowthBookProvider growthbook={gb} data-sentry-element="GrowthBookProvider" data-sentry-source-file="app.tsx">
          <Sentry.ErrorBoundary fallback={({
          error
        }) => <AppErrorDisplay error={error} nativeLink />} data-sentry-element="unknown" data-sentry-source-file="app.tsx">
            <SwitchThemeContext.Provider value={switchThemeContextValue} data-sentry-element="unknown" data-sentry-source-file="app.tsx">
              <AnonymousSaleProvider data-sentry-element="AnonymousSaleProvider" data-sentry-source-file="app.tsx">
                <LayoutOverrideProvider data-sentry-element="LayoutOverrideProvider" data-sentry-source-file="app.tsx">
                  <SoundProvider data-sentry-element="SoundProvider" data-sentry-source-file="app.tsx">
                    <ThemeObserverWrapper data-sentry-element="ThemeObserverWrapper" data-sentry-source-file="app.tsx" />
                    <ForceLogoutListener data-sentry-element="ForceLogoutListener" data-sentry-source-file="app.tsx" />
                    <RouterProvider data-sentry-element="RouterProvider" data-sentry-source-file="app.tsx" />
                  </SoundProvider>
                </LayoutOverrideProvider>
              </AnonymousSaleProvider>
            </SwitchThemeContext.Provider>
          </Sentry.ErrorBoundary>
        </GrowthBookProvider>
      </Provider>
    </HelmetProvider>;
};